import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { type LucideIcon, MoreHorizontal } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";

import { cn } from "@/shared/lib/cn";

export interface BottomNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

/**
 * Mobile-first primary navigation. Sticks to the bottom of the viewport,
 * thumb-reachable, with safe-area inset for iOS notched devices.
 *
 * Up to 5 items render directly. With 6+, the last slot collapses into a
 * "More" sheet that lists the overflow — keeps each visible slot wide
 * enough to read on a 360px viewport (iPhone SE / small Android).
 */
const VISIBLE_LIMIT = 5;

export function BottomNav({ items }: { items: BottomNavItem[] }) {
  const overflow = items.length > VISIBLE_LIMIT;
  const visible = overflow ? items.slice(0, VISIBLE_LIMIT - 1) : items;
  const hidden = overflow ? items.slice(VISIBLE_LIMIT - 1) : [];
  const slots = overflow ? VISIBLE_LIMIT : visible.length;

  return (
    <nav
      aria-label="Primary"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-surface/95 backdrop-blur-md pb-[env(safe-area-inset-bottom)] md:hidden"
    >
      <ul
        className="mx-auto grid w-full max-w-md"
        style={{ gridTemplateColumns: `repeat(${slots}, minmax(0, 1fr))` }}
      >
        {visible.map((item) => (
          <li key={item.to}>
            <NavItem item={item} />
          </li>
        ))}
        {overflow && (
          <li>
            <MoreSlot items={hidden} />
          </li>
        )}
      </ul>
    </nav>
  );
}

function NavItem({ item, onNavigate }: { item: BottomNavItem; onNavigate?: () => void }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "relative flex h-full min-h-[3.5rem] flex-col items-center justify-center gap-1 px-1 py-2 text-[11px] font-medium leading-tight transition-colors",
          isActive ? "text-primary" : "text-copy-muted hover:text-copy",
        )
      }
    >
      {({ isActive }) => (
        <>
          <span
            aria-hidden
            className={cn(
              "absolute inset-x-6 top-0 h-0.5 rounded-full transition-all",
              isActive ? "bg-primary" : "bg-transparent",
            )}
          />
          <Icon className="h-5 w-5 shrink-0" strokeWidth={isActive ? 2.25 : 1.75} />
          <span className="truncate max-w-full">{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

function MoreSlot({ items }: { items: BottomNavItem[] }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const activeOverflow = items.find((item) => isPathMatch(location.pathname, item.to));
  const isActive = Boolean(activeOverflow);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          className={cn(
            "relative flex h-full min-h-[3.5rem] w-full flex-col items-center justify-center gap-1 px-1 py-2 text-[11px] font-medium leading-tight transition-colors",
            isActive ? "text-primary" : "text-copy-muted hover:text-copy",
          )}
          aria-label="More navigation"
        >
          <span
            aria-hidden
            className={cn(
              "absolute inset-x-6 top-0 h-0.5 rounded-full transition-all",
              isActive ? "bg-primary" : "bg-transparent",
            )}
          />
          <MoreHorizontal
            className="h-5 w-5 shrink-0"
            strokeWidth={isActive ? 2.25 : 1.75}
          />
          <span className="truncate max-w-full">
            {activeOverflow?.label ?? "More"}
          </span>
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 animate-fade-in bg-overlay/60 backdrop-blur-sm md:hidden" />
        <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 md:hidden focus:outline-none">
          <div className="animate-fade-in-up rounded-t-2xl border-t border-border bg-surface-raised shadow-elevated pb-[env(safe-area-inset-bottom)]">
            <div className="flex items-center justify-center pt-2.5">
              <span aria-hidden className="h-1 w-10 rounded-full bg-border-strong" />
            </div>
            <div className="px-2 pb-3 pt-2">
              <Dialog.Title className="px-3 pb-2 pt-1 text-xs font-medium uppercase tracking-wider text-copy-muted">
                More
              </Dialog.Title>
              <Dialog.Description className="sr-only">
                Additional navigation destinations.
              </Dialog.Description>
              <ul className="space-y-1">
                {items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end
                        onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                          cn(
                            "flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-colors",
                            isActive
                              ? "bg-primary-light text-primary"
                              : "text-copy hover:bg-surface-sunken",
                          )
                        }
                      >
                        <Icon className="h-5 w-5 shrink-0" />
                        <span className="truncate">{item.label}</span>
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function isPathMatch(pathname: string, to: string): boolean {
  if (pathname === to) return true;
  return pathname.startsWith(to + "/");
}
